// Feature flags for the top-level features that can be turned off at
// deploy time. Reads Vite build-time env vars — set VITE_FEATURE_<KEY>_ENABLED
// to the literal string 'true' to enable the feature. Any other value
// (or missing entirely) leaves the feature disabled. Default = OFF — except
// the DEFAULT_ON features, which stay on unless set to 'false' (they predate
// the flags; only a slimmed build like the DRDC evaluation turns them off).
//
// The env-var reads MUST be written as literal `import.meta.env.VITE_FOO`
// references so Vite can statically inline them at build time — dynamic
// `import.meta.env[key]` access doesn't get replaced and would evaluate
// against `process.env` at runtime, which is undefined in the browser. So
// the mapping table is spelled out one line per flag.

export type FeatureKey =
    | 'tasks'
    | 'assets'
    | 'controlMeasures'
    | 'assetTypes'
    | 'sensorTypes'
    | 'rfc'
    | 'intel'
    | 'jipoe'
    | 'detections'
    | 'targets'
    | 'planEditor'
    | 'ocean3d'
    // The planning language on the chart: schedule scrubber, Quick plan /
    // Plan Starts, geo overlays and planned control measures.
    | 'dsl'
    // REPMUS acoustic-analysis tooling on the chart: glider / STANAG
    // contacts, heatmaps, CPA, datums, detection range, PF, asset lists.
    | 'analysis'
    // The Timeline page in the nav (removed for REPMUS 2026-09-06).
    | 'timeline'
    // The Chorus Assistant (chat) button and panel.
    | 'assistant'
    // Data-source modules in the operation setup (Admin): Jasco assets and
    // their allocations to operations.
    | 'jasco';

const FEATURE_ENV: Record<FeatureKey, string | undefined> = {
    tasks: import.meta.env.VITE_FEATURE_TASKS_ENABLED,
    assets: import.meta.env.VITE_FEATURE_ASSETS_ENABLED,
    controlMeasures: import.meta.env.VITE_FEATURE_CONTROL_MEASURES_ENABLED,
    assetTypes: import.meta.env.VITE_FEATURE_ASSET_TYPES_ENABLED,
    sensorTypes: import.meta.env.VITE_FEATURE_SENSOR_TYPES_ENABLED,
    rfc: import.meta.env.VITE_FEATURE_RFC_ENABLED,
    intel: import.meta.env.VITE_FEATURE_INTEL_ENABLED,
    jipoe: import.meta.env.VITE_FEATURE_JIPOE_ENABLED,
    detections: import.meta.env.VITE_FEATURE_DETECTIONS_ENABLED,
    targets: import.meta.env.VITE_FEATURE_TARGETS_ENABLED,
    planEditor: import.meta.env.VITE_FEATURE_PLAN_EDITOR_ENABLED,
    ocean3d: import.meta.env.VITE_FEATURE_OCEAN3D_ENABLED,
    dsl: import.meta.env.VITE_FEATURE_DSL_ENABLED,
    analysis: import.meta.env.VITE_FEATURE_ANALYSIS_ENABLED,
    timeline: import.meta.env.VITE_FEATURE_TIMELINE_ENABLED,
    assistant: import.meta.env.VITE_FEATURE_ASSISTANT_ENABLED,
    jasco: import.meta.env.VITE_FEATURE_JASCO_ENABLED,
};

const DEFAULT_ON: ReadonlySet<FeatureKey> = new Set<FeatureKey>(['planEditor', 'ocean3d', 'dsl', 'analysis', 'assistant']);

export function isFeatureEnabled(feature: FeatureKey): boolean {
    return DEFAULT_ON.has(feature) ? FEATURE_ENV[feature] !== 'false' : FEATURE_ENV[feature] === 'true';
}

/** Map of route name → feature key. Consumed by the router guard so a
 *  direct URL hit / deep link to a disabled feature bounces the user
 *  back to '/' instead of rendering the page under a hidden menu. */
export const routeFeature: Record<string, FeatureKey> = {
    // Tasks
    CatlTasks: 'tasks',
    TaskCreateManual: 'tasks',
    NodeHistory: 'tasks',
    // Assets
    CatlAssets: 'assets',
    AssetCreate: 'assets',
    AssetEdit: 'assets',
    AssetView: 'assets',
    ActiveSortie: 'assets',
    // Control Measures
    CatlControlMeasures: 'controlMeasures',
    ControlMeasureCreate: 'controlMeasures',
    ControlMeasureView: 'controlMeasures',
    ControlMeasureEdit: 'controlMeasures',
    // Asset Types
    CatlAssetTypes: 'assetTypes',
    AssetTypeCreate: 'assetTypes',
    AssetTypeEdit: 'assetTypes',
    // Sensor Types
    CatlSensorTypes: 'sensorTypes',
    // RFC (Request for Collection) — includes the Sync Matrix which is
    // captioned "Requests for Collection" in its route title.
    CatlRfc: 'rfc',
    CatlSyncMatrix: 'rfc',
    // Intel
    IntelSources: 'intel',
    // JIPOE
    Jipoe: 'jipoe',
    // Detections timeline
    Detections: 'detections',
    // Targets by day
    Targets: 'targets',
    // Planning-language editor
    PlanEditor: 'planEditor',
    // Ocean 3D cross-section
    Ocean3d: 'ocean3d',
};
